import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { PatientBookingConfirmation, BookingOverviewButton } from '../components/PatientBookingConfirmation';
import { AddToCalendarButton } from '../components/AddToCalendarButton';
import { type CalendarAppointment } from '../appointmentCalendar';

interface AppointmentBookedSummary extends CalendarAppointment {
  when: string;
  procedureId?: string;
  procedureType?: string;
  procedureName: string;
  practitionerName: string;
}

const storageKey = 'lastBookedAppointmentSummary';

const isExistingPatientBooking = () => {
  if (!localStorage.getItem('token')?.trim() || !localStorage.getItem('loginPatientId')?.trim()) return false;

  try {
    const steps = JSON.parse(localStorage.getItem('headerDefinition') ?? 'null');
    // Registration keeps "Persönliche Angaben" in step 4, even after receiving a patient ID.
    return Array.isArray(steps) && steps.length === 5 && steps[3] === 'Zusammenfassung';
  } catch {
    return false;
  }
};

const readStoredAppointmentSummary = (): AppointmentBookedSummary | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const summary = parsed as AppointmentBookedSummary;
    if (!summary.when || !summary.procedureName || !summary.practitionerName) {
      return null;
    }

    return summary;
  } catch {
    return null;
  }
};

const formatDate = (when?: string) => {
  if (!when) {
    return '--';
  }

  const date = new Date(when);
  if (Number.isNaN(date.getTime())) {
    return when;
  }

  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatTime = (when?: string) => {
  if (!when) {
    return '--';
  }

  const date = new Date(when);
  if (Number.isNaN(date.getTime())) {
    return when;
  }

  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SummaryIcon = ({ type }: { type: 'procedure' | 'practitioner' | 'date' | 'time' }) => {
  if (type === 'procedure') {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
        <rect x="5" y="6.5" width="14" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.5 10.5h4.5M8.5 13.5h2.5M15.5 10l-1.2 1.2-.8-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === 'practitioner') {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8.5" r="3.2" fill="currentColor" />
        <path d="M6.5 18.5c.8-3 3-4.7 5.5-4.7s4.7 1.7 5.5 4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'date') {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
        <rect x="4.5" y="6.5" width="15" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 4.5v4M16 4.5v4M4.5 10.5h15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v4l2.8 1.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

export function AppointmentBookedPage() {
  localStorage.setItem('headerPhase', '5');

  const location = useLocation();
  const navigate = useNavigate();
  const summaryFromState = (location.state as { appointment?: AppointmentBookedSummary } | null)?.appointment;
  const summary = useMemo(() => summaryFromState ?? readStoredAppointmentSummary(), [summaryFromState]);

  const handleOverview = () => {
    navigate('/appointments');
  };

  if (!summary?.when) {
    return (
      <div className="min-h-screen bg-[#f8f6f2] text-slate-900">
        <Header compact />
        <div className="px-6 py-10">
          <div className="mx-auto max-w-3xl rounded-[32px] border border-[#efe8dc] bg-white px-8 py-12 text-center shadow-[0_20px_40px_rgba(15,23,42,0.04)]">
            <h1 className="text-3xl font-semibold text-[#202737]">Kein Termin gefunden</h1>
            <p className="mt-4 text-base text-[#9da3af]">Die Daten für die Buchungsbestätigung sind aktuell nicht verfügbar.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isExistingPatientBooking()) {
    return (
      <PatientBookingConfirmation
        procedureId={summary.procedureId || localStorage.getItem('selectedProcedureId')?.trim() || ''}
        procedureType={summary.procedureType}
        procedureName={summary.procedureName}
        practitionerName={summary.practitionerName}
        date={formatDate(summary.when)}
        time={formatTime(summary.when)}
        calendarAppointment={summary}
        onOverview={handleOverview}
      />
    );
  }

  return (
    <div className="booking-confirmation min-h-screen bg-[#f8f6f2] text-slate-900">
      <Header compact />
      <div className="booking-confirmation__content px-4 py-6 sm:px-6 sm:py-10">
        <div className="booking-confirmation__card mx-auto max-w-[760px] rounded-[28px] border border-[#f0ebe2] bg-white px-5 py-10 shadow-[0_16px_40px_rgba(15,23,42,0.04)] sm:px-10 sm:py-14">
          <div className="mx-auto max-w-[520px] text-center">
            <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#eef9f0]">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#27c461] text-white">
                <svg className="h-6 w-6" viewBox="0 0 20 20" fill="none">
                  <path d="M5 10.5l3 3 7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            <h1 className="mt-8 text-[28px] font-semibold tracking-[-0.03em] text-[#202737] sm:text-[34px]">
              Termin erfolgreich gebucht
            </h1>
            <p className="mx-auto mt-4 max-w-[460px] text-[18px] leading-[1.45] text-[#9aa0ad]">
              Vielen Dank für Ihr Vertrauen. Wir haben Ihnen eine Bestätigungs-E-Mail mit allen Details zugesandt.
            </p>
          </div>

          <div className="booking-confirmation__summary mx-auto mt-10 max-w-[590px] rounded-[22px] border border-[#f0ebe2] bg-[#fcfcfb] px-6 py-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)] sm:px-7 sm:py-7">
            <p className="text-[13px] font-semibold text-[#d6bf8d]">Zusammenfassung</p>

            <div className="booking-confirmation__details mt-5 grid gap-x-6 gap-y-5 sm:grid-cols-2">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-white text-[#d1b47d] shadow-[0_6px_16px_rgba(15,23,42,0.05)]">
                  <SummaryIcon type="procedure" />
                </div>
                <div className="pt-0.5">
                  <p className="text-[15px] leading-5 text-[#a3a9b5]">Behandlung</p>
                  <p className="mt-1 text-[19px] font-semibold leading-6 text-[#2a3140]">{summary.procedureName}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-white text-[#d1b47d] shadow-[0_6px_16px_rgba(15,23,42,0.05)]">
                  <SummaryIcon type="practitioner" />
                </div>
                <div className="pt-0.5">
                  <p className="text-[15px] leading-5 text-[#a3a9b5]">Behandler:in</p>
                  <p className="mt-1 text-[19px] font-semibold leading-6 text-[#2a3140]">{summary.practitionerName}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-white text-[#d1b47d] shadow-[0_6px_16px_rgba(15,23,42,0.05)]">
                  <SummaryIcon type="date" />
                </div>
                <div className="pt-0.5">
                  <p className="text-[15px] leading-5 text-[#a3a9b5]">Datum</p>
                  <p className="mt-1 text-[19px] font-semibold leading-6 text-[#2a3140]">{formatDate(summary.when)}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] bg-white text-[#d1b47d] shadow-[0_6px_16px_rgba(15,23,42,0.05)]">
                  <SummaryIcon type="time" />
                </div>
                <div className="pt-0.5">
                  <p className="text-[15px] leading-5 text-[#a3a9b5]">Uhrzeit</p>
                  <p className="mt-1 text-[19px] font-semibold leading-6 text-[#2a3140]">{formatTime(summary.when)} Uhr</p>
                </div>
              </div>
            </div>
          </div>

          <div className="booking-confirmation__actions mx-auto mt-10 grid max-w-[590px] gap-4 sm:grid-cols-2">
            <BookingOverviewButton
              onClick={handleOverview}
              className="patient-booking-confirmation__overview rounded-[16px] border border-[#ebe5da] bg-white px-6 py-4 text-[18px] font-semibold text-[#404654] shadow-[0_8px_20px_rgba(19,19,19,0.03)]"
            />
            <AddToCalendarButton appointment={summary} />
          </div>

          <p className="booking-confirmation__contact mt-14 text-center text-[16px] text-[#b8bec8]">
            Haben Sie Fragen? Kontaktieren Sie uns unter <span className="font-semibold text-[#d0b37a]">052 624 46 25.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
